import Endpoints from "../api";
import debounce from "lodash/debounce";
import localStorage from "./localStorage";
import moment from 'moment';
import * as Actions from '../store/action-types';
import { SET_CURRENT_TICKET } from "../store/mutation-types";

/**
 * Get items from tickets
 *
 * @param tickets
 * @returns {*}
 */
export const getItems = (tickets) => {
    if (Array.isArray(tickets)) {
        return tickets.reduce((current, ticket) => {
            return current.concat(ticket.products);
        }, []);
    }

    return tickets.products;
};

/**
 * Calculate total of tickets to buy
 *
 * @param items
 * @returns {number}
 */
export const calculateQuantity = (items) => {
    return items.reduce((current, item) => {
        return current + item.quantity;
    }, 0);
};

/**
 * Calculate total for products to buy
 *
 * @param items
 * @returns {number}
 */
export const calculateTotal = (items) => items.reduce((current, item) => {
    return current + parseFloat(item.price);
}, 0);

export const pluck = (items, key) => items.reduce((current, item) => current.concat(item[key]), []);

/**
 * Get total for one or more tickets
 *
 * @param tickets
 * @returns {number}
 */
export const getTotal = (tickets) => {
    const items = getItems(tickets);
    return !items.length ? 0 : calculateTotal(items);
};

/**
 * Get quantity for one or more tickets
 *
 * @param tickets
 * @returns {number}
 */
export const getCount = (tickets) => {
    const items = getItems(tickets);
    return !items.length ? 0 : calculateQuantity(items);
};

/**
 * Handle cart updates to the server to save current state of the cart
 * after any change
 *
 * @param localCart
 * @param dispatch
 * @returns {*}
 */
const cartUpdate = (localCart, dispatch) => Endpoints.cart.update(localCart).then(remoteCart => {
    const {uuid, id, date} = remoteCart;
    localStorage.save('cart', {uuid, id, date});

    if (dispatch) dispatch(Actions.mergeLocalAndRemoteCart, {
        remoteCart
    });
}).catch(err => console.error(err));


/**
 * Function to update the cart after some time
 */
export const cartUpdater = debounce(cartUpdate, 500);

/**
 * Sync product quantity between tickets and cart. Depends
 * on who update the quantity(ticket or cart).
 *
 * @param payload - {product, cart, updateFrom, currentTicket}
 */
export const syncQuantity = (payload) => {
    const {product, cart, updateFrom, currentTicket, events} = payload;
    let p = null;
    console.log("Pload Sync", payload);
    if (product.pur_type === 'FIX') {
        // Since events are object like array, we look for a product coincidence
        // to update its quantity and reflect the change into the fix desktop
        // and mobile component
        if (events && Object.keys(events).length) {
            const fixTicket = Object.values(events).find(fixTicket => {
                if (!fixTicket.ticket) return false;

                return fixTicket.ticket.products.find(p => p.key === product.key);
            });

            if (fixTicket) {
                p = fixTicket.ticket.products.find(pr => pr.key === product.key);
            }
        }
    }
    else if (updateFrom === 'ticket') {
        // If ticket's product was updated, we sync the cart with its
        // quantity
        p = cart.find(pr => pr.product_id === product.product_id);
    } else if (currentTicket && currentTicket.id) {
        // Cart was updated, sync current ticket quantity with the
        // new cart quantity
        p = currentTicket.products.find(pr => pr.product_id === product.product_id);
    }

    if (p) p.quantity = product.quantity;
};

/**
 * Set ticket state to handle flexTicket UI updates
 *
 * @param state
 * @param ticket
 */
export const updateTicketState = (state, ticket) => {
    if (ticket) {
        const ticketQuantity = getCount(ticket);
        if (ticketQuantity) {
            state.ticket = ticket;
        } else {
            state.ticket = {};
        }
    }

    if (!state.cart.length || !cartHasFlexTickets(state.cart)) state.ticket = {};
};

/**
 * Check if the cart has at least one flex ticket
 *
 * @param cart
 * @returns {*|boolean}
 */
export const cartHasFlexTickets = (cart) => {
    return cart.some(product => product.pur_type === 'FLEX' && product.quantity > 0);
};

/**
 * Add unit price to products in tickets
 *
 * @param tickets
 * @returns {*}
 */
export const addUnitPriceToProduct = (tickets) => {
    return tickets.map(ticket => {
        ticket.products = addUnitPrice(ticket.products);

        return ticket;
    });
};

/**
 * Add unit price to products
 *
 * @param products
 * @returns {*}
 */
export const addUnitPrice = (products) => {
    if (Array.isArray(products)) {
        return products.map(p => ({...p, unit_price: p.grouped_price}));
    }

    return {...products, unit_price: products.grouped_price};
};

export const maxPriceFromProducts = products => {
    const prices = products.map(p => p.price);
    return Math.max(...prices);
};

export const maxPriceFromTicket = ticket => {
    if (!ticket) return 0;

    return maxPriceFromProducts(ticket.products);
};

const addFixInfoToProduct = (products, start_date, duration, ticket) => {
    return products.map(product => ({
            ...product,
            unit_price: product.price,
            start_date,
            end_date: moment(start_date, 'YYYY-MM-DD').add(duration - 1, 'days').format('YYYY-MM-DD'),
            title: `${ticket.ax_name} - ${product.person}`
        }
    )).map(addKeyToProduct);
};

export const arrayToObjectLikeArray = (fixTickets) => fixTickets.reduce((list, item) => {
    const ticket = item.ticket;

    return {
        ...list,
        [item.date]: {
            ...item, price: maxPriceFromTicket(ticket),
            ticket: ticket
                ? {...ticket, products: addFixInfoToProduct(ticket.products, item.date, item.duration, ticket)}
                : null
        }
    }
}, {});

export const buildKey = (product) => {
    if (!product.start_date) return product.product_id;

    const {start_date, end_date} = product;
    const date1 = moment(start_date, 'YYYY-MM-DD');
    const date2 = moment(end_date, 'YYYY-MM-DD');
    const duration = date2.diff(date1, 'days');

    return `${product.product_id}_${product.start_date}_${duration + 1}`;
};

export const addKeyToProduct = (product) => {
    return ({...product, key: buildKey(product)});
};

export const addKeyToProducts = (products) => {
    if (Array.isArray(products)) {
        return products.map(addKeyToProduct);
    }
    return addKeyToProduct(products);
};



// WEBPACK FOOTER //
// ./src/helpers/productHelper.js